import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { Check } from '@mui/icons-material';
import { gql } from 'api/graphql';
import DestinationFacilityFilter, {
  ALL_CAMPUS_FACILITIES
} from 'components/Campus/DestinationFacilityFilter';
import FacilitySelector from 'components/FacilitySelector/FacilitySelector';
import { taskCreatedByFacilityId } from 'pages/TaskApp/facilityFilter';
import { useCampusDestinations } from 'hooks/useCampusDestinations';
import { facilityDisplayName, facilityLabelById } from 'utils/campusLabels';
import { destinationFacilityId } from 'utils/destinationAvailability';
import {
  buildEditInput,
  LocationChoice,
  LocationKind,
  LocationOption,
  locationOptions,
  sameLocation,
  storedLocation
} from 'utils/movementTaskEdit';

const EDIT_MOVEMENT_TASK = `
  mutation EditMovementTask($input: EditMovementTaskInput!) {
    editMovementTask(input: $input) {
      uuid
    }
  }
`;

const KIND_LABELS: Record<LocationKind, string> = {
  yard_spot: 'Yard Spot',
  dock: 'Dock',
  facility_zone: 'Zone'
};

interface EditMovementTaskModalProps {
  open: boolean;
  task: any;
  onClose: () => void;
  /** Called once the edit is saved, to refresh the task list. */
  onSaved: () => void | Promise<void>;
}

/**
 * Edits where an open movement task takes its (empty) trailer from and to, anywhere in
 * the trailer's campus. A multi-facility campus gets a facility picker per end, and one
 * for the facility the task is created for (what the Task App lists it under).
 * flux re-runs the movement rules, moves the task's locks and its safety check; a change
 * of created-for facility alone re-runs nothing.
 */
const EditMovementTaskModal: React.FC<EditMovementTaskModalProps> = ({
  open,
  task,
  onClose,
  onSaved
}) => {
  const sourceFacilityId =
    task?.source_facility_id || task?.data?.source_facility_id || task?.facility_id;
  const destinations = useCampusDestinations(sourceFacilityId, open);
  const trailerUuid = task?.data?.trailer_uuid;

  const currentSource = useMemo(() => storedLocation(task?.data, 'from'), [task]);
  const currentDestination = useMemo(() => storedLocation(task?.data, 'to'), [task]);

  const [source, setSource] = useState<LocationChoice | null>(null);
  const [destination, setDestination] = useState<LocationChoice | null>(null);
  const [sourceKind, setSourceKind] = useState<LocationKind>('yard_spot');
  const [destinationKind, setDestinationKind] = useState<LocationKind>('yard_spot');
  // Facility picker per end; null follows the facility of the task's current end.
  const [sourceFacilityFilter, setSourceFacilityFilter] = useState<string | null>(null);
  const [destinationFacilityFilter, setDestinationFacilityFilter] = useState<
    string | null
  >(null);
  const currentCreatedBy = task ? taskCreatedByFacilityId(task) : '';
  const [createdBy, setCreatedBy] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every open starts from the task's own ends.
  useEffect(() => {
    if (!open) return;
    setSource(currentSource);
    setDestination(currentDestination);
    setSourceKind(currentSource?.kind || 'yard_spot');
    setDestinationKind(currentDestination?.kind || 'yard_spot');
    setSourceFacilityFilter(null);
    setDestinationFacilityFilter(null);
    setCreatedBy(currentCreatedBy);
    setError(null);
  }, [open, currentSource, currentDestination, currentCreatedBy]);

  const showFacility = destinations.isMultiFacility;
  // Facilities are named by their alias where they have one.
  const facilityLabel = (facilityId?: string | null, fallback = '') =>
    facilityDisplayName(facilityLabelById(destinations.facilities, facilityId)) ||
    fallback;
  const facilityOf = (choice: LocationChoice | null) =>
    choice ? destinationFacilityId(destinations, choice.kind, choice.uuid) : undefined;
  // Each picker opens on the facility its current end is at, else the whole campus.
  const sourceFacility =
    sourceFacilityFilter ?? facilityOf(currentSource) ?? ALL_CAMPUS_FACILITIES;
  const destinationFacility =
    destinationFacilityFilter ?? facilityOf(currentDestination) ?? ALL_CAMPUS_FACILITIES;
  const onlyAt = (facility: string) =>
    facility === ALL_CAMPUS_FACILITIES ? null : facility;

  const sourceOptions = withCurrent(
    locationOptions(destinations, sourceKind, 'source', {
      trailerUuid,
      facilityId: onlyAt(sourceFacility),
      current: currentSource,
      exclude: destination
    }),
    sourceKind,
    currentSource,
    task?.from_object?.name
  );

  const destinationOptions = withCurrent(
    locationOptions(destinations, destinationKind, 'destination', {
      trailerUuid,
      facilityId: onlyAt(destinationFacility),
      current: currentDestination,
      exclude: source
    }),
    destinationKind,
    currentDestination,
    task?.to_object?.name
  );

  // The campus lists decide the facility pickers (hidden for one facility), their
  // starting facility and the place labels, so the pickers wait for the first load
  // rather than redrawing as it lands. Reopening is instant: the lists are cached.
  const ready = destinations.facilities.length > 0 || !!destinations.error;

  const locationsChanged =
    !!source &&
    !!destination &&
    (!sameLocation(source, currentSource) ||
      !sameLocation(destination, currentDestination));
  const createdByChanged = !!createdBy && createdBy !== currentCreatedBy;
  const changed = locationsChanged || createdByChanged;

  const handleSave = async () => {
    if (!task || !changed) return;
    setSaving(true);
    setError(null);
    try {
      await gql(EDIT_MOVEMENT_TASK, {
        input: buildEditInput(
          task.uuid,
          currentSource,
          currentDestination,
          locationsChanged ? source : null,
          locationsChanged ? destination : null,
          currentCreatedBy,
          createdBy
        )
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to edit the task');
    } finally {
      setSaving(false);
    }
  };

  const renderPicker = (
    title: string,
    kind: LocationKind,
    setKind: (kind: LocationKind) => void,
    options: LocationOption[],
    value: LocationChoice | null,
    setValue: (value: LocationChoice | null) => void,
    current: LocationChoice | null,
    facility: string,
    setFacility: (facility: string) => void
  ) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      {/* Hidden by the component itself in a one-facility campus. */}
      <DestinationFacilityFilter
        facilities={destinations.facilities}
        value={facility}
        label={`${title} Facility`}
        disabled={saving}
        onChange={(next) => {
          setFacility(next);
          const at = facilityOf(value);
          if (next !== ALL_CAMPUS_FACILITIES && at && at !== next) setValue(null);
        }}
      />
      <ToggleButtonGroup
        value={kind}
        exclusive
        fullWidth
        size="small"
        onChange={(_, next) => {
          if (!next) return;
          setKind(next);
          // A choice of another kind is not on the new list; fall back to the current end.
          if (value?.kind !== next) setValue(current?.kind === next ? current : null);
        }}
        sx={{ mb: 1.5 }}
        disabled={saving}
      >
        {(Object.keys(KIND_LABELS) as LocationKind[]).map((k) => (
          <ToggleButton key={k} value={k}>
            {KIND_LABELS[k]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Autocomplete
        options={options}
        groupBy={
          showFacility
            ? (option) => facilityLabel(option.facilityId, option.facilityName)
            : undefined
        }
        value={options.find((option) => sameLocation(option, value)) || null}
        onChange={(_, option) =>
          setValue(option ? { kind: option.kind, uuid: option.uuid } : null)
        }
        getOptionLabel={(option) =>
          option.detail ? `${option.label} · ${option.detail}` : option.label
        }
        isOptionEqualToValue={(option, selected) => sameLocation(option, selected)}
        loading={destinations.loading}
        disabled={saving}
        noOptionsText={`No available ${KIND_LABELS[kind].toLowerCase()}s`}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={`Select ${KIND_LABELS[kind].toLowerCase()}`}
          />
        )}
      />
    </Box>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Move</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Trailer <strong>{task?.trailer?.trailer_number || 'Unknown'}</strong>
          {' · '}
          {task?.from_object?.name || 'Unknown'} → {task?.to_object?.name || 'Unknown'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {destinations.error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {destinations.error}
          </Alert>
        )}

        {!ready ? (
          <Box
            sx={{
              minHeight: 320,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading locations...
            </Typography>
          </Box>
        ) : (
          <>
            {showFacility && (
              <Box sx={{ mb: 3 }}>
                <FacilitySelector
                  facilities={destinations.facilities.map((f) => ({
                    id: f.facilityId,
                    name: facilityDisplayName({
                      name: f.facilityName,
                      facility_alias: f.facilityAlias
                    })
                  }))}
                  value={createdBy}
                  onChange={(event) => setCreatedBy(String(event.target.value))}
                  label="Created For"
                  fullWidth
                  disabled={saving}
                  sx={{ width: '100%' }}
                />
                <Typography variant="caption" color="text.secondary">
                  The Task App lists this move under this facility.
                </Typography>
              </Box>
            )}
            {renderPicker(
              'From',
              sourceKind,
              setSourceKind,
              sourceOptions,
              source,
              setSource,
              currentSource,
              sourceFacility,
              setSourceFacilityFilter
            )}
            {renderPicker(
              'To',
              destinationKind,
              setDestinationKind,
              destinationOptions,
              destination,
              setDestination,
              currentDestination,
              destinationFacility,
              setDestinationFacilityFilter
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !ready || !changed}
          startIcon={saving ? <CircularProgress size={16} /> : <Check />}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * The task's current end is always selectable, even when the campus lists leave it
 * out (a door at a facility the user cannot dock at, say).
 */
function withCurrent(
  options: LocationOption[],
  kind: LocationKind,
  current: LocationChoice | null,
  currentName?: string
): LocationOption[] {
  if (
    !current ||
    current.kind !== kind ||
    options.some((option) => sameLocation(option, current))
  ) {
    return options;
  }
  return [
    {
      ...current,
      label: `${currentName || 'Current location'} (current)`,
      detail: '',
      facilityId: '',
      facilityName: 'Current'
    },
    ...options
  ];
}

export default EditMovementTaskModal;
